// Helpers around the `device_data` object pushed by the Android monitoring
// service every ~10 minutes (see DEVICE-DATA.md).
//
// Per that contract, the dashboard treats any `device_data` whose
// `updated_at` is older than 30 minutes as stale — newer than that is
// "live", older is "may not reflect reality".

// 30 minutes in ms — the staleness threshold from DEVICE-DATA.md §2.
const STALE_THRESHOLD_MS = 30 * 60 * 1000;

/**
 * Compute a freshness summary for a `device_data.updated_at` timestamp.
 *
 * @param {number|string|undefined|null} updatedAt epoch ms
 * @returns {{ isMissing: boolean, isStale: boolean, ageMs: number, label: string }}
 *   - `isMissing` — there's no `updated_at` at all (pre-3.0 device or first-boot)
 *   - `isStale`   — older than 30 minutes
 *   - `ageMs`     — milliseconds since the push (0 if missing)
 *   - `label`     — human-readable "X min ago" / "no data yet"
 */
export function getFreshness(updatedAt) {
    if (updatedAt == null) {
        return { isMissing: true, isStale: false, ageMs: 0, label: 'no data yet' };
    }
    // Coerce because some flat fields (e.g. last_time_seen) ship as strings;
    // device_data.updated_at is a long, but be defensive anyway.
    const t = Number(updatedAt);
    if (!Number.isFinite(t)) {
        return { isMissing: true, isStale: false, ageMs: 0, label: 'no data yet' };
    }
    const ageMs = Math.max(0, Date.now() - t);
    return {
        isMissing: false,
        isStale: ageMs > STALE_THRESHOLD_MS,
        ageMs,
        label: humanizeAge(ageMs),
    };
}

/**
 * Quick predicate for "is this device_data stale?"
 * @param {number|string|undefined|null} updatedAt
 * @returns {boolean}
 */
export function isStale(updatedAt) {
    return getFreshness(updatedAt).isStale;
}

// ---------------------------------------------------------------------
// Palette helpers — DEVICE-DATA.md §7 thresholds, expressed as a tone
// string ('good' | 'warning' | 'critical' | 'neutral'). The Tailwind
// class lookup at the bottom keeps the colour decisions in one place.
// ---------------------------------------------------------------------

/** Storage tone from % free space. */
export function storageTone(percentFree) {
    if (percentFree == null) return 'neutral';
    if (percentFree < 15) return 'critical';
    if (percentFree < 30) return 'warning';
    return 'good';
}

/** RAM tone from % free RAM. */
export function ramTone(percentFree) {
    if (percentFree == null) return 'neutral';
    if (percentFree < 10) return 'critical';
    if (percentFree < 25) return 'warning';
    return 'good';
}

/** Network latency tone (ms). */
export function latencyTone(ms) {
    if (ms == null) return 'neutral';
    if (ms > 300) return 'critical';
    if (ms > 100) return 'warning';
    return 'good';
}

/** Packet loss tone (%). */
export function packetLossTone(pct) {
    if (pct == null) return 'neutral';
    if (pct > 5) return 'critical';
    if (pct > 0) return 'warning';
    return 'good';
}

/** CPU temperature tone (°C). */
export function cpuTempTone(c) {
    if (c == null) return 'neutral';
    if (c > 80) return 'critical';
    if (c > 60) return 'warning';
    return 'good';
}

/** Crash count tone (over 24h). */
export function crashesTone(n) {
    if (n == null) return 'neutral';
    if (n > 3) return 'critical';
    if (n >= 1) return 'warning';
    return 'good';
}

/** Composite health-status string → tone (DEVICE-DATA.md §4 mapping). */
export function healthStatusTone(status) {
    switch (status) {
        case 'good':     return 'good';
        case 'fair':     return 'warning';
        case 'warning':  return 'warning'; // fair/warning both render as yellow
        case 'critical': return 'critical';
        default:         return 'neutral';
    }
}

/**
 * Tailwind classes for a tone. Returns text + soft-bg variants keyed for
 * convenience, so a card can pick whichever it needs.
 */
export function toneClasses(tone) {
    switch (tone) {
        case 'good':
            return { text: 'text-green-400',  bg: 'bg-green-500/15',  ring: 'ring-green-500/30',  hex: '#22c55e' };
        case 'warning':
            return { text: 'text-yellow-400', bg: 'bg-yellow-500/15', ring: 'ring-yellow-500/30', hex: '#eab308' };
        case 'critical':
            return { text: 'text-red-400',    bg: 'bg-red-500/15',    ring: 'ring-red-500/30',    hex: '#ef4444' };
        default:
            return { text: 'text-gray-400',   bg: 'bg-gray-500/15',   ring: 'ring-gray-500/30',   hex: '#9ca3af' };
    }
}

/**
 * "5 min ago" / "2h ago" / "3d ago" — short, dashboard-friendly.
 * Anything under a second renders as "just now".
 */
export function humanizeAge(ageMs) {
    if (ageMs < 1000) return 'just now';
    const seconds = Math.floor(ageMs / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
}
